import { useField, useFormikContext } from 'formik';

import { DatePicker } from '../../../cdl/DatePicker/DatePicker';
import type { DatePickerProps } from '../../../cdl/DatePicker/DatePicker';
import { localStartOfDayAsUtcIso } from '../../helpers/dateFns.helper';
import { MILLISECONDS_IN_MINUTE } from '../../time.constants';

interface FormikDatePickerProps
    extends Pick<
        DatePickerProps,
        'label' | 'markAsRequired' | 'description' | 'placeholder' | 'caption' | 'CaptionIcon' | 'isClearable'
    > {
    name: string;
    onChange?: (date: string) => void;
}

export const FormikDatePicker = ({ name, onChange, ...rest }: FormikDatePickerProps) => {
    const [field, meta, helpers] = useField(name);
    const { validateOnChange } = useFormikContext();

    const onDateChange = (date: Date | undefined) => {
        helpers.setTouched(true, false);
        const value = date ? localStartOfDayAsUtcIso(date) : '';
        helpers.setValue(value, true);
        onChange?.(value);

        if (!validateOnChange && meta.error) {
            helpers.setError(undefined);
        }
    };

    const formatUtcIsoStringToLocalDate = (isoDate: string) => {
        const date = new Date(isoDate);
        const timezoneOffset = date.getTimezoneOffset() * MILLISECONDS_IN_MINUTE * -1;

        return new Date(date.getTime() - timezoneOffset);
    };

    return (
        <DatePicker
            {...rest}
            date={field.value ? formatUtcIsoStringToLocalDate(field.value) : undefined}
            onDateChange={onDateChange}
            negative={meta.touched && !!meta.error}
            caption={meta.touched && meta.error ? meta.error : rest.caption}
            onBlur={() => helpers.setTouched(true)}
        />
    );
};
